/**
 * Security utilities for the application
 * Focused on session management and input sanitization
 */

/**
 * Sanitize user input to prevent XSS attacks
 */
export function sanitizeInput(input: string): string {
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
}

/**
 * Check if a user session has been inactive for too long
 * @param lastActivityTime timestamp of last user activity
 * @param maxInactiveTime maximum allowed inactive time in ms
 */
export function isSessionInactive(lastActivityTime: number, maxInactiveTime: number = 4 * 60 * 60 * 1000): boolean {
  return Date.now() - lastActivityTime > maxInactiveTime;
}

/**
 * Update the last activity timestamp
 */
export function updateLastActivity(): void {
  localStorage.setItem('lastActivity', Date.now().toString());
}

/**
 * Get the last activity timestamp
 */
export function getLastActivity(): number {
  const lastActivity = localStorage.getItem('lastActivity');
  return lastActivity ? parseInt(lastActivity, 10) : Date.now();
}

/**
 * Clear session data comprehensively
 * Removes all client-side session information
 */
export function clearSessionData(): void {
  // Check if form is currently active before clearing data
  const isFormActive = localStorage.getItem('form-active') === 'true';
  const hasFormData = localStorage.getItem('admin-form-state-backup') || localStorage.getItem('program-draft');
  
  if (isFormActive && hasFormData) {
    console.log('Form is active with data, preserving form state during session cleanup');
    // Only clear auth-related data, preserve form data
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('sessionRestorationAttempted');
    
    // Clear sessionStorage (if any)
    sessionStorage.clear();
    
    // Only clear auth-related keys, not form data
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.toLowerCase().includes('auth') ||
        key.toLowerCase().includes('token') ||
        key.toLowerCase().includes('session') ||
        key.toLowerCase().includes('user') ||
        key.toLowerCase().includes('admin')
      ) && !key.toLowerCase().includes('form') && 
         !key.toLowerCase().includes('draft') &&
         !key.toLowerCase().includes('backup')) {
        keysToRemove.push(key);
      }
    }
    
    // Remove only auth-related keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`Removed authentication key: ${key}`);
    });
    
    console.log('Session data cleared (form data preserved)');
    return;
  }
  
  // Normal cleanup when form is not active
  localStorage.removeItem('lastActivity');
  localStorage.removeItem('sessionRestorationAttempted');
  
  // Clear sessionStorage (if any)
  sessionStorage.clear();
  
  // Force clear any remaining authentication-related data
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.toLowerCase().includes('auth') ||
      key.toLowerCase().includes('token') ||
      key.toLowerCase().includes('session') ||
      key.toLowerCase().includes('user') ||
      key.toLowerCase().includes('admin')
    )) {
      keysToRemove.push(key);
    }
  }
  
  // Remove all authentication-related keys
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    console.log(`Removed authentication key: ${key}`);
  });
  
  console.log('Session data cleared successfully');
}

/**
 * Force clear authentication cookies with retry logic
 * This ensures cookies are cleared even if server doesn't respond properly
 */
export async function forceClearAuthCookies(apiUrl: string, logoutEndpoint: string, maxRetries = 1): Promise<void> {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(`${apiUrl}${logoutEndpoint}`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        console.log(`Cookie cleanup successful on attempt ${attempt}`);
        return;
      } else if (response.status === 401) {
        // 401 is expected when session is already invalid - this is actually success
        console.log(`Cookie cleanup successful (session already invalid) on attempt ${attempt}`);
        return;
      } else if (response.status >= 400 && response.status < 500) {
        // Other 4xx errors are also expected during logout
        console.log(`Cookie cleanup completed (client error ${response.status} expected during logout)`);
        return;
      } else {
        console.warn(`Cookie cleanup attempt ${attempt} failed with status: ${response.status}`);
      }
    } catch (error) {
      console.warn(`Cookie cleanup attempt ${attempt} failed:`, error);
      if (attempt === maxRetries) {
        console.log('All cookie cleanup attempts completed (some may have failed, but this is expected)');
        return;
      }
      // Wait before retry (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
}

/**
 * Rate limit management utilities
 */
export const rateLimitManager = {
  /**
   * Check if we're currently rate limited
   */
  isRateLimited(): boolean {
    if (typeof window === 'undefined') return false;
    
    const lastRateLimit = localStorage.getItem('lastRateLimit');
    if (!lastRateLimit) return false;
    
    const timeSinceRateLimit = Date.now() - parseInt(lastRateLimit);
    const rateLimitCooldown = 60 * 1000; // 1 minute cooldown
    
    return timeSinceRateLimit < rateLimitCooldown;
  },
  
  /**
   * Get time remaining until rate limit expires
   */
  getTimeUntilReset(): number {
    if (typeof window === 'undefined') return 0;
    
    const lastRateLimit = localStorage.getItem('lastRateLimit');
    if (!lastRateLimit) return 0;
    
    const timeSinceRateLimit = Date.now() - parseInt(lastRateLimit);
    const rateLimitCooldown = 60 * 1000; // 1 minute cooldown
    
    return Math.max(0, rateLimitCooldown - timeSinceRateLimit);
  },
  
  /**
   * Set rate limit timestamp
   */
  setRateLimited(): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem('lastRateLimit', Date.now().toString());
  },
  
  /**
   * Clear rate limit state
   */
  clearRateLimit(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('lastRateLimit');
  },
  
  /**
   * Check if we should allow a request (not rate limited)
   */
  shouldAllowRequest(): boolean {
    return !this.isRateLimited();
  }
}; 